import React, { useEffect, useState } from "react";
import { api, downloadExport } from "@/lib/api";
import Markdown from "@/components/Markdown";
import StatsBar from "@/components/StatsBar";
import TeamPanel from "@/components/TeamPanel";
import RegulationAlerts from "@/components/RegulationAlerts";
import ChuckConsentPanel from "@/components/ChuckConsentPanel";
import PaywallModal from "@/components/PaywallModal";
import { toast } from "sonner";
import {
  Folder,
  FolderPlus,
  FileText,
  Trash2,
  X,
  ArrowLeft,
  Download,
  Loader2,
  Plus,
  Users,
  Zap,
} from "lucide-react";

export const PROJECT_IDS = {
  createBtn: "project-create-btn",
  nameInput: "project-name-input",
  deviceInput: "project-device-input",
  descInput: "project-desc-input",
  saveBtn: "project-save-btn",
  card: "project-card",
  openBtn: "project-open-btn",
  deleteBtn: "project-delete-btn",
  detailNewDoc: "project-detail-new-doc",
  detailBackBtn: "project-detail-back",
};

export default function ProjectsPanel({ onCreateDraft }) {
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({ name: "", device_name: "", description: "", product_type: "device" });
  const [detail, setDetail] = useState(null); // full project with documents
  const [viewDoc, setViewDoc] = useState(null);
  const [fullDocs, setFullDocs] = useState([]);   // documents WITH content, for RegulationAlerts
  const [quota, setQuota] = useState(null);
  const [paywallOpen, setPaywallOpen] = useState(false);
  const [paywallReason, setPaywallReason] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const [{ data: projs }, { data: q }] = await Promise.all([
        api.get("/projects"),
        api.get("/billing/quota").catch(() => ({ data: null })),
      ]);
      setProjects(projs);
      setQuota(q);
    } catch {
      toast.error("Could not load projects");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const openCreate = () => {
    // Pre-block on the client if we already know they're at quota
    if (quota?.quota_reached) {
      setPaywallReason(`You've hit your ${quota.plan?.toUpperCase()} plan limit`);
      setPaywallOpen(true);
      return;
    }
    setCreating(true);
  };

  const save = async () => {
    if (!form.name.trim()) return toast.error("Project name required");
    try {
      const { data } = await api.post("/projects", form);
      setProjects([data, ...projects]);
      setCreating(false);
      setForm({ name: "", device_name: "", description: "", product_type: "device" });
      // refresh quota
      api.get("/billing/quota").then(({ data: q }) => setQuota(q)).catch(() => {});
      toast.success("Project created");
    } catch (e) {
      const detail = e?.response?.data?.detail;
      // 402 Payment Required → paywall
      if (e?.response?.status === 402 && detail?.quota_reached) {
        setCreating(false);
        setPaywallReason(detail.message || `You've hit your ${detail.current_plan?.toUpperCase() || "FREE"} plan limit`);
        setPaywallOpen(true);
        // refresh quota so the modal shows the right numbers
        api.get("/billing/quota").then(({ data: q }) => setQuota(q)).catch(() => {});
      } else {
        toast.error(typeof detail === "string" ? detail : (detail?.message || "Failed to create project"));
      }
    }
  };

  const open = async (id) => {
    try {
      const { data } = await api.get(`/projects/${id}`);
      setDetail(data);
      // Fetch full doc contents in parallel so RegulationAlerts can extract citations
      const contents = await Promise.all(
        (data.documents || []).map((d) =>
          api.get(`/documents/${d.id}`).then((r) => r.data).catch(() => null)
        )
      );
      setFullDocs(contents.filter(Boolean));
    } catch {
      toast.error("Could not open project");
    }
  };

  const refreshDetail = async () => {
    if (!detail) return;
    try {
      const { data } = await api.get(`/projects/${detail.id}`);
      setDetail(data);
    } catch {}
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this project? Documents inside will remain in your library.")) return;
    try {
      await api.delete(`/projects/${id}`);
      setProjects(projects.filter((p) => p.id !== id));
      toast.success("Project deleted");
    } catch { toast.error("Delete failed"); }
  };

  const openDoc = async (id) => {
    try {
      const { data } = await api.get(`/documents/${id}`);
      setViewDoc(data);
    } catch { toast.error("Could not open document"); }
  };

  // ---------- Project detail ----------
  if (detail) {
    return (
      <div>
        <button
          data-testid={PROJECT_IDS.detailBackBtn}
          onClick={() => { setDetail(null); setFullDocs([]); load(); }}
          className="mb-6 inline-flex items-center gap-2 overline text-slate-500 hover:text-slate-900"
        >
          <ArrowLeft className="h-3 w-3" /> All projects
        </button>

        <div className="mb-8 flex flex-wrap items-end justify-between gap-4">
          <div>
            <div className="overline mb-1 text-[#2563EB]">PROJECT</div>
            <h1 className="font-serif text-4xl font-semibold tracking-tight">{detail.name}</h1>
            {detail.device_name && (
              <div className="mt-2 overline text-slate-500">DEVICE · {detail.device_name}</div>
            )}
            {detail.description && (
              <p className="mt-3 max-w-2xl text-sm text-slate-600">{detail.description}</p>
            )}
            {(detail.members?.length > 0 || !detail.is_owner) && (
              <div className="mt-3 inline-flex items-center gap-2 text-xs text-slate-500">
                <Users className="h-3.5 w-3.5" />
                {detail.is_owner
                  ? `${(detail.members || []).length} teammate${(detail.members || []).length === 1 ? "" : "s"}`
                  : "Shared with you"}
              </div>
            )}
          </div>
          <button
            data-testid={PROJECT_IDS.detailNewDoc}
            onClick={() => onCreateDraft?.({ project_id: detail.id, device_name: detail.device_name })}
            className="inline-flex items-center gap-2 bg-[#2563EB] px-4 py-2.5 text-sm font-semibold text-white hover:bg-[#1D4ED8]"
          >
            <Plus className="h-4 w-4" /> New document
          </button>
          <button
            data-testid="project-assemble-btn"
            onClick={async () => {
              try {
                const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/projects/${detail.id}/assemble`, {
                  method: "POST", credentials: "include",
                });
                if (!res.ok) { const j = await res.json().catch(() => ({})); throw new Error(j.detail || "Assemble failed"); }
                const blob = await res.blob();
                const url = window.URL.createObjectURL(blob);
                const a = document.createElement("a");
                a.href = url;
                a.download = `${(detail.name || "submission_package").replace(/[^a-z0-9]+/gi, "_")}_submission_package.pdf`;
                document.body.appendChild(a); a.click(); a.remove();
                window.URL.revokeObjectURL(url);
                toast.success("Submission package downloaded");
              } catch (e) { toast.error(e.message || "Assemble failed"); }
            }}
            className="inline-flex items-center gap-2 border border-slate-300 bg-transparent px-4 py-2.5 text-sm font-semibold text-slate-900 hover:border-[#2563EB]"
          >
            <Download className="h-4 w-4" /> Assemble PDF
          </button>
        </div>

        {/* Regulation freshness alerts */}
        {fullDocs.length > 0 && (
          <div className="mb-8">
            <RegulationAlerts documents={fullDocs} />
          </div>
        )}

        {/* Team invites */}
        <div className="mb-8">
          <TeamPanel project={detail} onUpdate={refreshDetail} />
        </div>

        {detail.documents?.length === 0 ? (
          <div className="border border-slate-200 bg-white p-10 text-center">
            <FileText className="mx-auto mb-4 h-8 w-8 text-slate-300" strokeWidth={1.5} />
            <div className="overline text-slate-500">NO DOCUMENTS YET</div>
            <p className="mt-2 text-sm text-slate-500">
              Click <b className="text-slate-900">New document</b> to draft the first file for this device.
            </p>
          </div>
        ) : (
          <div className="grid gap-px bg-slate-100 sm:grid-cols-2 xl:grid-cols-3">
            {detail.documents.map((d) => (
              <div key={d.id} className="group flex flex-col justify-between bg-[#FAFAF7] p-5 transition-colors hover:bg-slate-50">
                <div>
                  <div className="mb-3 flex items-center justify-between">
                    <span className="font-mono text-[10px] text-[#2563EB]">/{d.doc_type}</span>
                    <StatusBadge status={d.metadata?.status} />
                  </div>
                  <div className="font-serif text-base font-semibold leading-tight text-slate-900">
                    {d.title}
                  </div>
                  <div className="overline mt-2 text-slate-400">{(d.created_at || "").slice(0, 10)}</div>
                </div>
                <div className="mt-6 flex items-center gap-2">
                  <button
                    onClick={() => openDoc(d.id)}
                    className="flex-1 border border-slate-200 py-2 text-xs font-medium text-slate-700 hover:border-[#2563EB] hover:text-slate-900"
                  >
                    Open
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}

        {viewDoc && <DocModal doc={viewDoc} onClose={() => setViewDoc(null)} />}
      </div>
    );
  }

  // ---------- Project list ----------
  return (
    <div>
      <StatsBar />
      <div className="mb-6">
        <ChuckConsentPanel />
      </div>
      <div className="mb-8 flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="overline mb-2 text-slate-500">SUBMISSION PROJECTS</div>
          <h1 className="font-serif text-3xl font-semibold tracking-tight lg:text-4xl">
            One device. One project. All the files.
          </h1>
          <p className="mt-2 max-w-xl text-sm text-slate-500">
            Bundle your 510(k), DHF, Risk Management, IFU, and cybersecurity docs together per device.
          </p>
        </div>
        {!creating && (
          <div className="flex items-center gap-3">
            {quota && (
              <div
                data-testid="quota-badge"
                className={`flex items-center gap-2 border px-3 py-1.5 text-[11px] ${
                  quota.quota_reached
                    ? "border-amber-300 bg-amber-50 text-amber-800"
                    : "border-slate-200 bg-white text-slate-600"
                }`}
              >
                <span className="font-mono font-semibold">{quota.projects_used} / {quota.projects_max}</span>
                <span className="uppercase tracking-wider">{quota.plan}</span>
                {quota.quota_reached && (
                  <button
                    data-testid="quota-badge-upgrade"
                    onClick={() => { setPaywallReason(`You've hit your ${quota.plan?.toUpperCase()} plan limit`); setPaywallOpen(true); }}
                    className="inline-flex items-center gap-1 border border-amber-400 bg-white px-1.5 py-0.5 font-semibold uppercase text-amber-800 hover:bg-amber-100"
                  >
                    <Zap className="h-2.5 w-2.5" /> Upgrade
                  </button>
                )}
              </div>
            )}
            <button
              data-testid={PROJECT_IDS.createBtn}
              onClick={openCreate}
              className="inline-flex items-center gap-2 bg-[#2563EB] px-4 py-2.5 text-sm font-semibold text-white hover:bg-[#1D4ED8]"
            >
              <FolderPlus className="h-4 w-4" /> New project
            </button>
          </div>
        )}
      </div>

      <PaywallModal
        open={paywallOpen}
        onClose={() => setPaywallOpen(false)}
        quota={quota}
        reason={paywallReason}
      />

      {creating && (
        <div className="mb-8 border border-slate-200 bg-white p-6">
          <div className="overline mb-4 text-[#2563EB]">NEW PROJECT</div>

          {/* Product type radio — device vs drug */}
          <div className="mb-5">
            <label className="overline mb-2 block text-slate-500">Product type</label>
            <div className="grid gap-3 md:grid-cols-2">
              {[
                { code: "device", title: "Medical Device", sub: "510(k), PMA, De Novo, IFU, DHF, CE / EU MDR" },
                { code: "drug",   title: "Drug",           sub: "IND, NDA, ANDA, Drug Label / PI, CMC, OTC" },
              ].map((opt) => {
                const active = form.product_type === opt.code;
                return (
                  <button
                    key={opt.code}
                    type="button"
                    data-testid={`project-product-type-${opt.code}`}
                    onClick={() => setForm({ ...form, product_type: opt.code })}
                    className={`border px-4 py-3 text-left transition-all ${
                      active
                        ? "border-[#2563EB] bg-[#2563EB]/10"
                        : "border-slate-200 bg-[#FAFAF7] hover:border-slate-300"
                    }`}
                  >
                    <div className={`text-sm font-semibold ${active ? "text-white" : "text-slate-700"}`}>
                      {opt.title}
                    </div>
                    <div className="mt-0.5 text-[11px] text-slate-500">{opt.sub}</div>
                  </button>
                );
              })}
            </div>
          </div>

          <div className="grid gap-4 md:grid-cols-2">
            <div>
              <label className="overline mb-2 block text-slate-500">Project name *</label>
              <input
                data-testid={PROJECT_IDS.nameInput}
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder={form.product_type === "drug" ? "SubmiRx v1 Submission" : "CardioSense v2 Submission"}
                className="w-full border border-slate-200 bg-[#FAFAF7] px-3 py-2.5 text-sm focus:border-[#2563EB] focus:outline-none"
              />
            </div>
            <div>
              <label className="overline mb-2 block text-slate-500">
                {form.product_type === "drug" ? "Drug name (brand or INN)" : "Device name"}
              </label>
              <input
                data-testid={PROJECT_IDS.deviceInput}
                value={form.device_name}
                onChange={(e) => setForm({ ...form, device_name: e.target.value })}
                placeholder={form.product_type === "drug" ? "e.g., Sertraline HCl 50 mg tablets" : "CardioSense Pulse Oximeter"}
                className="w-full border border-slate-200 bg-[#FAFAF7] px-3 py-2.5 text-sm focus:border-[#2563EB] focus:outline-none"
              />
            </div>
            <div className="md:col-span-2">
              <label className="overline mb-2 block text-slate-500">Description</label>
              <textarea
                data-testid={PROJECT_IDS.descInput}
                value={form.description}
                rows={2}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
                placeholder={form.product_type === "drug" ? "API, dosage form, route, indication, RLD if generic…" : "Short summary of the device and the submission goal…"}
                className="w-full resize-none border border-slate-200 bg-[#FAFAF7] px-3 py-2.5 text-sm focus:border-[#2563EB] focus:outline-none"
              />
            </div>
          </div>
          <div className="mt-4 flex gap-3">
            <button
              data-testid={PROJECT_IDS.saveBtn}
              onClick={save}
              className="bg-[#2563EB] px-4 py-2 text-sm font-semibold text-white hover:bg-[#1D4ED8]"
            >
              Create project
            </button>
            <button
              onClick={() => { setCreating(false); setForm({ name: "", device_name: "", description: "", product_type: "device" }); }}
              className="border border-slate-200 px-4 py-2 text-sm text-slate-600 hover:border-slate-300 hover:text-slate-900"
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {loading ? (
        <div className="flex items-center gap-2 text-slate-500"><Loader2 className="h-4 w-4 animate-spin" /> Loading…</div>
      ) : projects.length === 0 ? (
        <div className="border border-slate-200 bg-white p-10 text-center">
          <Folder className="mx-auto mb-4 h-8 w-8 text-slate-300" strokeWidth={1.5} />
          <div className="overline text-slate-500">NO PROJECTS YET</div>
          <p className="mt-2 text-sm text-slate-500">Create your first project to bundle all the files for a device.</p>
        </div>
      ) : (
        <div className="grid gap-px bg-slate-100 sm:grid-cols-2 xl:grid-cols-3">
          {projects.map((p) => (
            <div
              key={p.id}
              data-testid={PROJECT_IDS.card}
              className="group flex flex-col justify-between bg-[#FAFAF7] p-5 transition-colors hover:bg-slate-50"
            >
              <div>
                <div className="mb-3 flex items-center justify-between">
                  <Folder className="h-5 w-5 text-[#2563EB]" strokeWidth={1.5} />
                  <div className="flex items-center gap-2">
                    <span
                      data-testid={`project-card-type-${p.product_type || "device"}`}
                      className={`overline border px-1.5 py-0.5 ${
                        (p.product_type || "device") === "drug"
                          ? "border-purple-400/50 text-purple-300"
                          : "border-cyan-400/40 text-cyan-300"
                      }`}
                    >
                      {(p.product_type || "device") === "drug" ? "DRUG" : "DEVICE"}
                    </span>
                    <span className="overline text-slate-400">
                      {p.doc_count} {p.doc_count === 1 ? "DOC" : "DOCS"}
                    </span>
                  </div>
                </div>
                <div className="font-serif text-lg font-semibold leading-tight text-slate-900">{p.name}</div>
                {p.device_name && <div className="overline mt-2 text-slate-500">{p.device_name}</div>}
                {p.description && <p className="mt-3 line-clamp-2 text-sm text-slate-500">{p.description}</p>}
              </div>
              <div className="mt-6 flex items-center gap-2">
                <button
                  data-testid={PROJECT_IDS.openBtn}
                  onClick={() => open(p.id)}
                  className="flex-1 border border-slate-200 py-2 text-xs font-medium text-slate-700 hover:border-[#2563EB] hover:text-slate-900"
                >
                  Open project
                </button>
                <button
                  data-testid={PROJECT_IDS.deleteBtn}
                  onClick={() => remove(p.id)}
                  className="border border-slate-200 p-2 text-slate-500 hover:border-red-500/60 hover:text-red-400"
                >
                  <Trash2 className="h-3.5 w-3.5" />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function StatusBadge({ status }) {
  if (!status) return null;
  const map = {
    complete:  { c: "border-emerald-400/40 text-emerald-300", l: "COMPLETE" },
    truncated: { c: "border-amber-400/40 text-amber-300",     l: "TRUNCATED" },
    streaming: { c: "border-blue-400/40 text-blue-300",       l: "STREAMING" },
    error:     { c: "border-red-400/40 text-red-300",         l: "ERROR" },
  };
  const s = map[status] || { c: "border-slate-200 text-slate-500", l: status.toUpperCase() };
  return <span className={`border px-1.5 py-0.5 font-mono text-[9px] tracking-wider ${s.c}`}>{s.l}</span>;
}

function DocModal({ doc, onClose }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/70 p-4" onClick={onClose}>
      <div
        className="relative flex max-h-[92vh] w-full max-w-4xl flex-col border border-slate-200 bg-[#FAFAF7]"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between border-b border-slate-200 px-6 py-4">
          <div className="min-w-0 flex-1">
            <div className="overline text-[#2563EB]">/{doc.doc_type}</div>
            <div className="truncate font-serif text-lg font-semibold">{doc.title}</div>
          </div>
          <div className="flex shrink-0 items-center gap-2">
            <button
              onClick={() => downloadExport(doc.title, doc.content, "pdf")}
              className="inline-flex items-center gap-1.5 border border-slate-200 px-3 py-1.5 text-xs font-medium hover:border-[#2563EB]"
            >
              <Download className="h-3.5 w-3.5" /> PDF
            </button>
            <button
              onClick={() => downloadExport(doc.title, doc.content, "docx")}
              className="inline-flex items-center gap-1.5 border border-slate-200 px-3 py-1.5 text-xs font-medium hover:border-[#2563EB]"
            >
              <Download className="h-3.5 w-3.5" /> DOCX
            </button>
            <button onClick={onClose} className="border border-slate-200 p-1.5 hover:border-slate-300">
              <X className="h-4 w-4" />
            </button>
          </div>
        </div>
        <div className="flex-1 overflow-y-auto p-6 lg:p-10">
          <Markdown>{doc.content}</Markdown>
        </div>
      </div>
    </div>
  );
}
